import { useCallback, useEffect, useId, useRef } from 'react';
import type { JSX, KeyboardEvent, KeyboardEventHandler, MouseEvent, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import { Spinner } from './ProgressBar';

/**
 * The two modal dialogs — a port of the pair in `ui/settings/SettingsScreen.kt`
 * (`RemoveKeyConfirmDialog` and `RemovingKeyDialog`), which are Material 3
 * `AlertDialog`s as this app configures them.
 *
 * Material gives Compose the scrim, the focus trap, the back-press dismissal and
 * the "not dismissible" flag for free. On the web none of that is free, so it is
 * built here — and it is the half that matters most, because a modal that does
 * not trap focus is a modal only for people who can see it.
 *
 * That plumbing is what the rest of the app imports from this file: the scrim,
 * the focus trap, and the arrow-key arithmetic a roving tabindex needs. The
 * language sheet is a separate component and uses all three.
 */

/**
 * Everything that is tabbable *right now* inside `container`, in DOM order.
 *
 * `tabIndex >= 0` rather than the usual `:not([tabindex="-1"])` selector,
 * because the language sheet uses a roving tabindex: its rows are `<button>`s
 * that all match `button:not([disabled])` while only one of them is reachable
 * with Tab. Reading the resolved `tabIndex` property is the only way to tell
 * those apart, and getting it wrong would park initial focus on the first
 * language rather than the one that is selected.
 *
 * The size check drops anything hidden by CSS. Nothing here does that today; it
 * costs one comparison and prevents the classic bug where Tab appears to do
 * nothing because focus went to an invisible node.
 */
function tabbableWithin(container: HTMLElement): HTMLElement[] {
  const candidates = container.querySelectorAll<HTMLElement>(
    'a[href], button, input, select, textarea, [tabindex]',
  );
  return Array.from(candidates).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.tabIndex >= 0 &&
      (element.offsetWidth > 0 || element.offsetHeight > 0),
  );
}

export interface ModalFocus {
  /** Put this on the modal container — the element carrying `role` and `aria-modal`. */
  ref: RefObject<HTMLDivElement | null>;
  /** Put this on the same element. It is the Tab trap. */
  onKeyDown: KeyboardEventHandler<HTMLDivElement>;
}

/**
 * Focus containment for a modal, shared with `LanguagePickerSheet`.
 *
 * Three jobs, all of which Compose's `Dialog`/`ModalBottomSheet` do implicitly:
 *
 * 1. **Move focus in on open.** Onto the first tabbable control, or onto the
 *    container itself when there is none (the busy dialog has no controls at
 *    all) — otherwise focus stays on the trigger behind the scrim and the first
 *    Tab walks straight out of the modal.
 * 2. **Keep it in.** Tab from the last control wraps to the first, Shift+Tab
 *    from the first wraps to the last.
 * 3. **Put it back on close.** Onto the element that opened the modal, so a
 *    keyboard or VoiceOver user resumes where they left off instead of at the
 *    top of the page. Skipped when that element has left the document — the
 *    "remove device key" flow tears down the screen the button lived on, and
 *    focusing a detached node silently drops focus to `<body>`.
 *
 * @param onEscape called on the Escape key, or `null` for a modal that must not
 *   be dismissible — Android's
 *   `DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false)`.
 */
export function useModalFocus(onEscape: (() => void) | null): ModalFocus {
  const ref = useRef<HTMLDivElement>(null);

  // Held in a ref so the Escape listener is registered exactly once, on mount,
  // and still calls the newest callback. Re-registering it on every render would
  // be harmless today and a leak the first time a caller passes an inline
  // closure that changes identity mid-teardown.
  const escapeRef = useRef(onEscape);
  useEffect(() => {
    escapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    // Read before anything is focused: React has not moved focus yet at this
    // point, so this is still the control that opened the modal.
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const container = ref.current;
    if (container !== null) {
      const first = tabbableWithin(container)[0];
      (first ?? container).focus();
    }

    // On `document`, not on the container: an external keyboard is the only way
    // to press Escape on an iPhone, and by then focus may legitimately be on the
    // container itself, which some browsers do not bubble key events from in the
    // way a control would.
    const onDocumentKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      const escape = escapeRef.current;
      if (escape === null) return;
      event.preventDefault();
      escape();
    };
    document.addEventListener('keydown', onDocumentKeyDown);

    return () => {
      document.removeEventListener('keydown', onDocumentKeyDown);
      if (trigger !== null && trigger.isConnected) trigger.focus();
    };
    // Mount/unmount only. The modal is mounted when it opens and unmounted when
    // it closes — its callers render it conditionally — so "open" and "mounted"
    // are the same event and there is nothing to react to in between.
  }, []);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Tab') return;
    const container = ref.current;
    if (container === null) return;

    const items = tabbableWithin(container);
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) {
      // No controls to move between — swallowing Tab is what keeps focus from
      // walking out into the page behind the scrim.
      event.preventDefault();
      return;
    }

    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  return { ref, onKeyDown };
}

/**
 * Where an arrow key moves within a list of `length` items, or `null` for "not
 * an arrow key" and for an index that is not in the list.
 *
 * The shape of a roving tabindex, which this app has two of: the language sheet
 * in `LanguagePickerSheet` and the bottom tab bar in `MainShell` — which is why
 * it sits in this file, beside {@link useModalFocus}, rather than in either of
 * them. Both were carrying their own copy
 * of the same body — the ArrowDown/Right → +1 and ArrowUp/Left → −1 chain, the
 * `(index + step + length) % length` wrap, and the guard for a caller whose
 * current item is not in the list. Only "what is the list" ever differed, and a
 * copy that drifted would leave one of the two groups wrapping the wrong way or
 * not wrapping at all.
 *
 * Both axes, on purpose. Neither list is reliably vertical or horizontal — the
 * tab bar is a row, the sheet is a column, and an operator with a paired
 * keyboard has no reason to know which one this code thinks it is. WAI-ARIA's
 * pattern for a single-axis widget is to accept its own axis; accepting both
 * costs one comparison and is what the two existing handlers already did.
 *
 * It wraps rather than clamping, which is the whole reason it returns a number
 * instead of a boolean: at the last language, ArrowDown goes back to the first.
 * A list of one wraps to itself, which is the correct no-op.
 *
 * Pure — no DOM, no React. The caller owns "which element is that index", which
 * is the part that genuinely differs: the sheet queries its rows, the tab bar
 * indexes a static route array.
 */
export function stepIndex(key: string, index: number, length: number): number | null {
  const step =
    key === 'ArrowDown' || key === 'ArrowRight'
      ? 1
      : key === 'ArrowUp' || key === 'ArrowLeft'
        ? -1
        : 0;
  if (step === 0) return null;
  // `index < 0` is the `findIndex` miss — focus is somewhere else entirely, and
  // moving it from a position we do not have would be a jump the user did not
  // ask for.
  if (index < 0 || index >= length) return null;
  return (index + step + length) % length;
}

/**
 * The scrim behind a modal.
 *
 * Two elements rather than one, and `components.css` says why: `opacity` on an
 * ancestor applies to its descendants too, so a dialog nested inside a
 * 32%-opaque scrim would itself be 32% opaque. The scrim is a sibling that
 * carries the opacity, the layer above it carries the content.
 *
 * Both are portalled to `<body>`. `position: fixed` resolves against the nearest
 * ancestor with a `transform`, `filter` or `backdrop-filter` rather than the
 * viewport, and this app has several — the tab crossfade and the two `<canvas>`
 * decorations — plus a `.module`/`.screen-content` chain that clips overflow. A
 * dialog rendered in place would end up scrolled, clipped, or offset by whatever
 * happened to be transformed above it.
 */
export function ModalScrim(): JSX.Element {
  return <div className="scrim" aria-hidden="true" />;
}

/**
 * Material 3's `AlertDialog`, as this app configures it: a centred card on
 * `surfaceContainerHigh` (pinned to `--bg-surface-variant` in `Theme.kt`,
 * because Material otherwise derives that ramp from its own baseline palette and
 * opens a grey card in the middle of a midnight-blue screen), an optional tinted
 * icon, a title, a body and a pair of text buttons.
 *
 * **Cancel sits on the left and in the neutral colour; the destructive action is
 * on the right.** The safe way out should be the easy one to hit. Cancel is also
 * first in the DOM, which means it is where {@link useModalFocus} puts focus on
 * open — the same reasoning applied to the keyboard.
 *
 * The card is always centre-aligned. Compose only centres the title when an icon
 * is present, but both dialogs Android shows here pass one, so the centred form
 * is the only one that has ever appeared and `.dialog` commits to it.
 */
export function AlertDialog({
  icon,
  title,
  body,
  confirmText,
  onConfirm,
  cancelText,
  onDismiss,
  destructive,
}: {
  icon?: IconName;
  title: string;
  body: string;
  confirmText: string;
  onConfirm(): void;
  cancelText: string;
  onDismiss(): void;
  destructive?: boolean;
}): JSX.Element {
  const titleId = useId();
  const bodyId = useId();
  const { ref, onKeyDown } = useModalFocus(onDismiss);

  // Only a press that lands on the layer itself is "outside the dialog". Without
  // the target check, a drag that starts on the body text and ends on the
  // backdrop closes the dialog, which on a touch screen is most of them.
  const onBackdropClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) onDismiss();
  };

  return createPortal(
    <>
      <ModalScrim />
      <div className="modal-layer" onClick={onBackdropClick}>
        <div
          className="dialog"
          // `alertdialog`, not `dialog`: both of these interrupt the user to
          // report something, which is exactly the distinction the role makes.
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          tabIndex={-1}
          ref={ref}
          onKeyDown={onKeyDown}
        >
          {icon === undefined ? null : (
            // `--icon-prominent`, matching Material's own dialog icon size. The
            // colour is `.dialog__icon`'s own `--error`, which is what both
            // dialogs in this app want — this used to take an `iconColor` prop
            // whose one caller passed exactly that default back in.
            <Icon name={icon} size={24} className="dialog__icon" />
          )}
          <h2 id={titleId} className="t-headline-small dialog__title">
            {title}
          </h2>
          <p id={bodyId} className="t-body-medium dialog__body">
            {body}
          </p>
          <div className="dialog__actions">
            <button type="button" className="t-label-large text-btn" onClick={onDismiss}>
              {cancelText}
            </button>
            <button
              type="button"
              className={
                destructive === true
                  ? 't-label-large text-btn text-btn--danger'
                  : 't-label-large text-btn'
              }
              onClick={onConfirm}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/**
 * The dialog shown while something is being torn down — Android's
 * `RemovingKeyDialog`.
 *
 * **Not dismissible, deliberately**: no backdrop click, no Escape, no confirm
 * button. Removing the device key stops a run that may be mid-submit and waits
 * for it to actually be down; there is no halfway state to return to once that
 * has started, and Android says the same thing with
 * `DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false)`.
 * Passing `null` to {@link useModalFocus} is what expresses that here.
 *
 * The spinner keeps turning under `prefers-reduced-motion` (slowed, not stopped
 * — see the end of `components.css`). It is not decoration: it is the only thing
 * on screen saying the work is still in flight, and a frozen one reads as a hung
 * app, which is precisely the misreading that invites the second tap this dialog
 * exists to prevent.
 */
export function BusyDialog({ title, body }: { title: string; body: string }): JSX.Element {
  const titleId = useId();
  const bodyId = useId();
  const { ref, onKeyDown } = useModalFocus(null);

  return createPortal(
    <>
      <ModalScrim />
      {/* No click handler on the layer: tapping outside must do nothing. */}
      <div className="modal-layer">
        <div
          className="dialog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          aria-busy="true"
          tabIndex={-1}
          ref={ref}
          onKeyDown={onKeyDown}
        >
          {/* Android draws a CircularProgressIndicator in the icon slot, at
              IconSize.prominent and tinted NeonCyan. `.c-accent` is that cyan,
              and it reaches the spinner through `currentColor` — the component
              sets no colour of its own. */}
          <Spinner prominent className="c-accent" />
          <h2 id={titleId} className="t-headline-small dialog__title">
            {title}
          </h2>
          <p id={bodyId} className="t-body-medium dialog__body">
            {body}
          </p>
        </div>
      </div>
    </>,
    document.body,
  );
}
